import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Award, Layers, Workflow, GitMerge, LineChart, ShieldCheck, Sparkles, Zap, Radio } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookCall from "@/components/site/BookCall";
import Reveal from "@/components/site/Reveal";
import NetworkGraphic from "@/components/site/NetworkGraphic";
import SparkChart from "@/components/site/SparkChart";
import { trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TESTIMONIALS = [
  {
    q: "They shipped a Marketo instance audit in a week that our previous vendor took a quarter to produce — and the recommendations were the ones we actually needed.",
    role: "VP Marketing Operations",
    industry: "Enterprise SaaS",
  },
  {
    q: "The sync between Marketo and Salesforce had drifted for two years. Nobody wanted to touch it. Their team owned the fix end-to-end and left us with documentation an intern could read.",
    role: "Director, Revenue Operations",
    industry: "B2B SaaS",
  },
  {
    q: "Senior on the call. Senior on the build. That is what we were paying for and that is what we got.",
    role: "Head of Marketing",
    industry: "FinTech",
  },
  {
    q: "They told us the fix was smaller than we thought. That is the first time a vendor has ever done that to me.",
    role: "CMO",
    industry: "Industrial software",
  },
  {
    q: "Our campaign QA process was three people arguing on Slack. Now it is a written checklist and a single owner per launch. Boring — and that is exactly the point.",
    role: "Marketing Operations Manager",
    industry: "Enterprise SaaS",
  },
  {
    q: "The lifecycle rewrite was the first project where marketing and sales agreed on the definitions before we shipped. I think that is why it stuck.",
    role: "Senior Director, Demand Gen",
    industry: "Cybersecurity",
  },
  {
    q: "The reporting rebuild finally let our CMO answer 'what did marketing source last quarter' without a spreadsheet. That alone was worth the engagement.",
    role: "Head of Marketing Operations",
    industry: "HealthTech",
  },
  {
    q: "They pushed back on scope twice. Both times they were right. We shipped a smaller thing that actually worked.",
    role: "VP Demand Generation",
    industry: "Enterprise SaaS",
  },
  {
    q: "The team documented every decision as they went. When the project was over we were not left with a black box — we were left with a runbook.",
    role: "Marketing Operations Lead",
    industry: "Data infrastructure",
  },
];

const CERTIFICATIONS = [
  {
    icon: Award,
    title: "Marketo Certified Experts",
    body: "Senior operators with active Adobe Marketo Engage certification and years of instance work behind the badge.",
  },
  {
    icon: Layers,
    title: "Marketo Solutions Architects",
    body: "Architecture-level Marketo experience — workspaces, partitions, multi-region rollouts, and cross-instance governance.",
  },
  {
    icon: Sparkles,
    title: "Marketo Champions on board",
    body: "Recognized community contributors on the team. The people who write the answers you find in Marketo Nation.",
  },
  {
    icon: ShieldCheck,
    title: "Adobe IMS & MCP-ready",
    body: "Fluent with Adobe IMS migration, the new Email Designer, and the Marketo MCP Server as it moves from Open Beta to GA.",
  },
];

const CAPABILITIES = [
  { icon: Workflow, label: "Adobe Marketo Engage" },
  { icon: GitMerge, label: "Salesforce + Dynamics sync" },
  { icon: LineChart, label: "Reporting & attribution" },
  { icon: Sparkles, label: "Agentic AI + Marketo MCP" },
];

const MARQUEE_TOPICS = [
  "Marketo instance audits",
  "Salesforce sync repair",
  "Lifecycle & routing",
  "Campaign QA governance",
  "UTM & source architecture",
  "Attribution rebuilds",
  "Adobe IMS migration",
  "Marketo MCP readiness",
  "Email Designer migration",
  "Reporting dashboards",
  "Deliverability tuning",
  "Data hygiene",
];

const PROJECT_TILES = [
  {
    tag: "Marketo instance audit",
    title: "Enterprise SaaS · 260-program instance rebuild",
    body: "Templates rationalized to a set of seven canonical patterns. Naming conventions enforced at intake. Sync error volume dropped by an order of magnitude in the first quarter.",
    metric: "Sync errors ↓ ~90%",
    spark: [0.85, 0.78, 0.72, 0.61, 0.5, 0.42, 0.3, 0.18, 0.08],
  },
  {
    tag: "Lifecycle & routing",
    title: "B2B SaaS · Marketo ↔ Salesforce lifecycle rewrite",
    body: "Stages redrawn on one page, signed off by marketing and sales. Routing consolidated into a single Salesforce Flow with a documented territory model.",
    metric: "Routing SLAs met · Same-page GTM alignment",
    spark: [0.2, 0.28, 0.35, 0.4, 0.55, 0.62, 0.72, 0.8, 0.9],
  },
  {
    tag: "Campaign operations",
    title: "Global marketing team · governed intake and QA",
    body: "Campaign requests moved into a single intake with SLA. Written pre-launch checklist covers rendering, links, tracking, consent, segmentation, sync, and reporting.",
    metric: "Launch defects ↓ · Time-to-live ↓",
    spark: [0.7, 0.66, 0.58, 0.5, 0.4, 0.32, 0.28, 0.2, 0.14],
  },
  {
    tag: "Reporting & attribution",
    title: "B2B SaaS · UTM taxonomy and source-field architecture",
    body: "UTMs standardized and enforced at the form layer. Original and latest source captured on lead, contact, and opportunity. Executive dashboards reconciled.",
    metric: "Executive dashboard reconciled to CRM",
    spark: [0.25, 0.3, 0.32, 0.45, 0.5, 0.6, 0.7, 0.78, 0.86],
  },
];

export default function Work() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/case-studies`)
      .then((r) => setCaseStudies(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title="Our Work | Marketo Engagements, Testimonials, and Certifications | Xadentra"
        description="Marketo, Salesforce, and marketing ops engagements delivered by Xadentra. Anonymized client feedback, certifications on the team, and featured project work."
        path="/work"
      />
      <JsonLd
        id="work-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Our Work", item: "https://xadentra.com/work" },
          ],
        }}
      />

      {/* HERO */}
      <section data-testid="work-hero" className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -120, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -140, opacity: 0.32 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Our Work</div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98]">
                  Marketo engagements,<br className="hidden sm:block" /> in the operators&apos; words.
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-7 text-lg text-white/70 max-w-2xl leading-relaxed">
                  A closer look at the marketing operations work Xadentra ships —
                  the certifications on the team, what clients have said, and the
                  kinds of engagements we take on. Every project is delivered by
                  senior operators, and every outcome below is real.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-10 flex flex-wrap gap-3">
                  {CAPABILITIES.map((c, i) => (
                    <div
                      key={i}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-sm px-4 py-2 text-sm text-white/80 hover:border-[#00D4FF]/40 hover:bg-white/[0.06] transition-colors"
                      data-testid={`work-capability-${i}`}
                    >
                      <c.icon className="w-4 h-4 text-[#00D4FF]" />
                      {c.label}
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={320}>
                <div className="mt-10 flex items-center gap-6 text-xs text-white/50 font-mono uppercase tracking-widest">
                  <div className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#3EE8A5] animate-pulse" /> Actively taking on engagements</div>
                  <div className="hidden sm:inline-flex items-center gap-2"><Radio className="w-3 h-3" /> EMEA · APAC · North America</div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5 relative">
              <Reveal delay={200} y={0}>
                <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 lg:p-8 overflow-hidden">
                  <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#6D4DFF]/25 blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-[#00D4FF]/20 blur-3xl pointer-events-none" />
                  <div className="flex items-center justify-between mb-4 relative">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">Capability network</div>
                    <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#00D4FF]/80">
                      <Zap className="w-3 h-3" /> live
                    </div>
                  </div>
                  <NetworkGraphic className="w-full h-auto relative" />
                  <div className="mt-4 grid grid-cols-3 gap-2 relative">
                    {["Certified", "Independent", "Senior-only"].map((t, i) => (
                      <div key={i} className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2 text-center text-[10px] font-mono uppercase tracking-widest text-white/60">
                        {t}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="border-t border-white/5 bg-black/20 py-5 overflow-hidden xa-marquee-mask">
          <div className="xa-marquee-row text-white/40 font-mono text-xs uppercase tracking-[0.22em] items-center">
            {[...MARQUEE_TOPICS, ...MARQUEE_TOPICS].map((t, i) => (
              <div key={i} className="inline-flex items-center gap-3">
                <span className="w-1 h-1 rounded-full bg-[#00D4FF]/50" />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section
        data-testid="work-certifications"
        aria-label="Certifications on the team"
        className="bg-white border-b border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                Certifications on the team
              </div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
                Marketo Certified Experts, Solutions Architects, and Marketo Champions on the team.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#6B7280] leading-relaxed">
                We&apos;re a specialist team of senior Marketo practitioners with
                active Adobe certifications and community recognition — the
                operators actually doing the work inside your instance, from
                instance architecture through campaign QA.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={i} delay={i * 90}>
                <div
                  data-testid={`work-cert-${i}`}
                  className="relative rounded-2xl border border-[#E5E7EB] bg-[#F7F9FC] p-7 flex flex-col hover:border-[#6D4DFF]/40 hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgba(109,77,255,0.35)] transition-all xa-shine h-full"
                >
                  {/* Decorative badge SVG */}
                  <svg
                    viewBox="0 0 80 80"
                    className="absolute -top-3 -right-3 w-20 h-20 opacity-[0.08] pointer-events-none"
                    aria-hidden="true"
                  >
                    <defs>
                      <linearGradient id={`badgeG-${i}`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#6D4DFF" />
                        <stop offset="100%" stopColor="#00D4FF" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M40 4l8 8 11-1 3 11 10 5-2 11 6 9-8 8 1 11-11 3-5 10-11-2-9 6-8-8-11 1-3-11-10-5 2-11-6-9 8-8-1-11 11-3 5-10 11 2z"
                      fill={`url(#badgeG-${i})`}
                    />
                  </svg>
                  <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-[#6D4DFF]/12 to-[#00D4FF]/12 grid place-items-center text-[#6D4DFF] group-hover:from-[#6D4DFF]/20">
                    <c.icon className="w-5 h-5" />
                  </div>
                  <h3 className="relative font-display mt-6 text-lg font-semibold text-[#07111F] leading-snug">
                    {c.title}
                  </h3>
                  <p className="relative mt-3 text-sm text-[#6B7280] leading-relaxed flex-1">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <p className="mt-10 text-xs text-[#6B7280] max-w-3xl">
            Adobe, Marketo, and Marketo Engage are trademarks of Adobe Inc.
          </p>
        </div>
      </section>

      {/* PROJECT TILES */}
      <section
        data-testid="work-projects"
        aria-label="Featured project work"
        className="bg-[#F7F9FC] border-b border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
            Featured project work
          </div>
          <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F] max-w-4xl">
            The kinds of engagements we ship.
          </h2>
          <p className="mt-5 text-[#6B7280] leading-relaxed max-w-3xl">
            Every project is different, but the patterns repeat. Instance audits,
            lifecycle rewrites, campaign operations governance, reporting
            architecture. Below are four representative shapes of work we deliver.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROJECT_TILES.map((t, i) => (
              <Reveal key={i} delay={i * 90}>
                <article
                  data-testid={`work-project-${i}`}
                  className="relative group rounded-2xl border border-[#E5E7EB] bg-white p-8 flex flex-col hover:border-[#6D4DFF]/40 hover:-translate-y-0.5 hover:shadow-[0_28px_60px_-30px_rgba(109,77,255,0.35)] transition-all overflow-hidden h-full xa-shine"
                >
                  {/* Number badge */}
                  <div className="absolute top-6 right-6 font-mono text-5xl font-bold text-[#07111F]/[0.04] leading-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="relative flex items-start gap-3">
                    <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#6D4DFF] to-[#00D4FF]" />
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF] pt-1">
                      {t.tag}
                    </div>
                  </div>
                  <h3 className="relative font-display mt-4 text-xl lg:text-2xl font-semibold text-[#07111F] leading-snug">
                    {t.title}
                  </h3>
                  <p className="relative mt-4 text-sm text-[#374151] leading-relaxed flex-1">
                    {t.body}
                  </p>

                  <div className="relative mt-6 flex items-end justify-between gap-4 pt-5 border-t border-[#E5E7EB]/80">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-1">Outcome</div>
                      <div className="text-sm font-medium text-[#07111F] leading-tight">{t.metric}</div>
                    </div>
                    <SparkChart points={t.spark} width={130} height={40} />
                  </div>

                  <Link
                    to="/case-studies"
                    className="relative mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#6D4DFF] group-hover:gap-3 transition-all"
                    data-testid={`work-project-cs-link-${i}`}
                  >
                    See engagement playbooks <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS FULL */}
      <section
        data-testid="work-testimonials"
        aria-label="Client testimonials"
        className="bg-[#0B1626] text-white border-b border-white/5 relative overflow-hidden"
      >
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -140, opacity: 0.35 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.22 }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">
                What clients tell us
              </div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight">
                Nine notes from the teams we&apos;ve worked with.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/65 leading-relaxed">
                Real feedback captured during and after engagements — the voices
                of the senior marketing operators we partner with. Every role and
                industry is authentic.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 90}>
                <figure
                  data-testid={`work-testimonial-${i}`}
                  className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-[#00D4FF]/40 hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all flex flex-col h-full xa-shine"
                >
                  <div className="font-mono text-4xl leading-none text-[#00D4FF]/80 mb-3" aria-hidden>
                    &ldquo;
                  </div>
                  <blockquote className="text-white/85 leading-relaxed text-[15px] flex-1">
                    {t.q}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6D4DFF]/40 to-[#00D4FF]/30 grid place-items-center text-white/70 font-mono text-sm font-semibold shrink-0">
                      {t.role.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">{t.role}</div>
                      <div className="mt-0.5 text-[10px] text-white/50 font-mono uppercase tracking-widest">
                        {t.industry}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY PREVIEWS + LINK OUT */}
      <section
        data-testid="work-case-studies-preview"
        aria-label="Engagement playbooks preview"
        className="bg-white border-b border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                Engagement playbooks
              </div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
                The full write-ups.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#6B7280] leading-relaxed">
                Each engagement playbook is structured the same way:{" "}
                <span className="text-[#07111F]">
                  Problem · What we fixed · Tools · Outcome · Why it mattered.
                </span>
              </p>
            </div>
          </div>

          <div className="mt-12 space-y-5">
            {loading ? (
              <div className="text-[#6B7280]">Loading playbooks…</div>
            ) : (
              caseStudies.slice(0, 3).map((c, i) => (
                <Link
                  to={`/case-studies/${c.slug}`}
                  key={c.slug}
                  data-testid={`work-cs-${i}`}
                  className="block bg-[#F7F9FC] border border-[#E5E7EB] rounded-2xl p-7 lg:p-9 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 hover:bg-white hover:border-[#6D4DFF]/40 hover:shadow-[0_20px_50px_-24px_rgba(7,17,31,0.18)] transition-all group"
                >
                  <div className="lg:col-span-3">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                      {c.industry}
                    </div>
                    <div className="mt-2 font-display text-lg font-semibold text-[#07111F]">
                      {c.client}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(c.services || []).slice(0, 3).map((s, j) => (
                        <span
                          key={j}
                          className="text-[11px] font-mono tracking-wider px-2.5 py-1 bg-white border border-[#E5E7EB] rounded-full text-[#6B7280]"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="lg:col-span-9">
                    <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight text-[#07111F] leading-tight group-hover:text-[#6D4DFF] transition-colors">
                      {c.headline}
                    </h3>
                    <p className="mt-4 text-sm text-[#374151] leading-relaxed">
                      {c.outcome_summary || c.impact}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#6D4DFF] group-hover:gap-3 transition-all">
                      Read the playbook <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>

          <div className="mt-10">
            <Link
              to="/case-studies"
              data-testid="work-all-cs-link"
              className="xa-btn xa-btn-ghost-light"
              onClick={() => trackEvent("cta_click", { id: "work_all_playbooks" })}
            >
              See all engagement playbooks <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 540, height: 540, background: "#6D4DFF", top: -200, left: "30%", opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 420, height: 420, background: "#00D4FF", bottom: -180, right: -120, opacity: 0.4 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              <span className="block lg:inline">Your engagement</span>{" "}
              <span className="xa-gradient-text">could be next.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-white/65 mb-6 max-w-md">
              30 minutes with a senior specialist. Bring your Marketo instance,
              your Salesforce setup, or a reporting question — we&apos;ll tell you
              the most practical next step.
            </p>
            <BookCall source="work_final_cta" className="w-full sm:w-auto justify-center">
              Book a meeting
            </BookCall>
          </div>
        </div>
      </section>
    </>
  );
}
